import React, { useEffect } from "react";
import { useGlobalContext } from "state/GlobalContext";
import { Form, FormField, FormRow } from "components/common/forms";
import { Guest } from "models";
import Section from "page-components/Section";
import { Heading } from "theme-ui";
import Services from "services/Services";
import AddressSection from "./AddressSection";
import useMount from "hooks/useMount";

type Props = {
  guest: Guest,
  parkId: string,
}
const GuestForm: React.FC<Props> = ({ guest, parkId }) => {
  const fields = Guest.Model.fields;
  const [form] = Form.useForm<Guest>();
  const [{ dispatch }] = useGlobalContext();
  const email = Form.useWatch(fields.email.name, form);
  const phone = Form.useWatch(fields.phone.name, form);

  useEffect(() => {
    if (email && !phone) {
      fields.phone.rules = [{ required: false }];
    }
    if (!email && phone) {
      fields.email.rules = [{ required: false }];
    }
  }, [email, phone, fields]);

  useMount(() => {
    if (guest) {
      form.setFieldsValue(guest);
      form.setFieldsValue({ useMailingAsBilling: true });
    }
  });

  const tryCreateGuest = async (values: Guest) => {
    if (values.firstName && values.lastName && (values.email || values.phone)) {
      const payload = Guest.Model.buildPayload(values, parkId);
      const response = await Services.Guests.createOrUpdate(payload);
      const guest = Guest.Model.mapModel(response);
      dispatch({ type: 'SET_GUEST', payload: { guest: guest }});
      form.setFieldsValue(guest);
    }
  };

  const onBlur = async () => {
    const values = form.getFieldsValue();
    const model = Guest.new(values);
    if (!guestIsEqual(model, guest)) {
      tryCreateGuest(model);
      dispatch({ type: 'SET_GUEST', payload: { guest: model }});
    }
  }

  return (
    <Form form={form}
      onBlur={onBlur}
      layout="vertical"
      autoComplete="off"
    >
      <Heading>Guest details</Heading>
      <FormField field={fields.id} hidden />
      <Section maxW={380}>
        <FormRow colSpans={[12, 12]} gap={16}>
          <FormField field={fields.firstName} />
          <FormField field={fields.lastName} />
        </FormRow>
        <FormRow colSpans={[12, 12]} gap={16}>
          <FormField field={fields.email} disabled={!!guest?.id} />
          <FormField field={fields.phone} />
        </FormRow>
        <AddressSection parentForm={form} initialValues={guest} />
      </Section>
    </Form>
  );
}

function guestIsEqual(guestA: Guest, guestB: Guest) {
  const fields = Guest.Model.fields;
  const fieldSet = [
    fields.firstName.name, fields.lastName.name, fields.email.name, fields.phone.name,
    fields.address.name, fields.address2.name, fields.country.name, fields.state.name, fields.city.name, fields.postalCode.name,
  ];
  const condition = guestA && guestB && fieldSet.every(f => guestA[f] === guestB[f]);
  return condition;
}

export default GuestForm;
